import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Pencil } from "lucide-react";
import { errMsg } from "../lib/api";

const inp = "w-full border rounded-lg px-3 py-2 text-sm";
const list = (s) => s.split(/\n|,/).map((x) => x.trim()).filter(Boolean);

function View({ u }) {
  return (
    <div className="space-y-1.5" data-testid="understanding-view">
      {u.what_it_is && <p><b>What you do:</b> {u.what_it_is}</p>}
      {u.offerings?.length > 0 && <p><b>What you offer:</b> {u.offerings.join(", ")}</p>}
      {u.audience && <p><b>Who it's for:</b> {u.audience}</p>}
      {u.primary_goal && <p><b>Main goal of the site:</b> {u.primary_goal}</p>}
      {u.personality?.length > 0 && <p><b>Personality:</b> {u.personality.join(", ")}</p>}
    </div>);
}

function Form({ u, onSave, onCancel, busy }) {
  const [d, setD] = useState({ ...u, offerings: (u.offerings || []).join("\n"), personality: (u.personality || []).join(", ") });
  const f = (k, label, rows, ph) => (
    <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">{label}</span>
      <textarea className={inp} rows={rows} value={d[k] || ""} placeholder={ph} onChange={(e) => setD({ ...d, [k]: e.target.value })} data-testid={`understanding-${k}`} /></label>);
  return (
    <div className="space-y-3" data-testid="understanding-form">
      {f("what_it_is", "What your business does", 2, "e.g. A mobile sail repair workshop that visits marinas")}
      {f("offerings", "What you offer (one per line)", 3)}
      {f("audience", "Who it's for", 2)}
      {f("primary_goal", "Main goal of the website", 2, "e.g. Get sailors to book a visit")}
      {f("personality", "Brand personality (comma separated)", 1, "e.g. friendly, expert, outdoorsy")}
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => onSave({ ...d, offerings: list(d.offerings).slice(0, 8), personality: list(d.personality).slice(0, 6) })} className="btn-emerald !py-2" data-testid="understanding-save">{busy ? "Saving…" : "Save my corrections"}</button>
        <button onClick={onCancel} className="text-sm underline" data-testid="understanding-cancel">Cancel</button>
      </div>
    </div>);
}

export function UnderstandingCard({ api, pid, plan, sections, locked, onSaved, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/projects/${pid}/understanding`).then((r) => setData(r.data)).catch(() => setData(null)); }, [api, pid]);
  if (!data) return null;
  const save = async (u) => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${pid}/understanding`, u);
      setData(r.data); setEdit(false);
      toast.success(r.data.preview_stale ? "Saved. Refresh your preview to rebuild the website from your corrections." : "Saved. Your website preview will be built from this.");
      onSaved?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const title = (id) => sections?.find((s) => s.id === id)?.title || id;
  return (
    <div className="rounded-xl border bg-white px-4 py-3 text-sm" data-testid="design-brief">
      <button className="w-full flex items-center justify-between font-medium text-[#062a20]" onClick={() => setOpen(!open)} data-testid="design-brief-toggle">
        How we understood your business {data.source === "customer" && <span className="ml-2 text-[11px] text-emerald-700" data-testid="understanding-confirmed">Confirmed by you</span>}
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 grid md:grid-cols-2 gap-4" data-testid="design-brief-body">
        <div className="space-y-3">
          {edit ? <Form u={data.understanding} busy={busy} onSave={save} onCancel={() => setEdit(false)} /> : <View u={data.understanding} />}
          {!edit && !locked && <button onClick={() => setEdit(true)} className="text-xs inline-flex items-center gap-1 underline" data-testid="understanding-edit"><Pencil className="w-3.5 h-3.5" />Correct this</button>}
          {!edit && <p className="text-[11px] text-slate-500">If anything is wrong, correct it before your preview is created (or refresh the preview afterwards) so the website is built around your business.</p>}
        </div>
        {plan?.length > 0 && <ol className="space-y-1.5 list-decimal pl-5" data-testid="design-brief-plan"><p className="text-xs font-semibold text-slate-500 -ml-5">Section plan (staff only)</p>
          {plan.map((x) => <li key={x.section_id}><b>{title(x.section_id)}</b> — {x.purpose}</li>)}</ol>}
      </div>}
    </div>);
}
